import React from 'react';
import {
  AlignItems,
  BackgroundColor,
  BackgroundPosition,
  BackgroundRepeat,
  BackgroundSize,
  classNames,
  Container,
  ContainerWidth,
  Display,
  FlexDirection,
  FontFamily,
  Grid,
  GridItem,
  GridSpan,
  Height,
  IWithResponsiveProps,
  JustifyContent,
  Margin,
  Padding,
  Width,
  withResponsiveBrowserOnly,
} from '@snoam/pinata';
import { BackgroundLoader } from '@snoam/mono-background-loader';
import { RouteComponentProps, withRouter } from 'react-router-dom';

interface ILayoutLeftColumnProps
  extends IWithResponsiveProps,
    RouteComponentProps {
  backgroundImage: string;
  className?: any;
  showTopImageMobile: boolean;
  loading?: boolean;
  backgroundColor: BackgroundColor;
  backgroundPosition: BackgroundPosition;
  coreHeaderHeight: string;
}

interface ILayoutLeftColumnState {
  loading: boolean;
  backgroundImage: string;
}

const styleClass = {
  figure: (mobile: boolean, showTopImageMobile: boolean) =>
    classNames(
      { [Display.HIDDEN]: !showTopImageMobile && mobile },
      Margin.M_0,
      Display.LG_HIDDEN,
    ),
  grid: classNames(Padding.XS_P_0, Width.MD_W_MODAL),
  image: Display.BLOCK,
  inner: {
    container: classNames(
      Display.INLINE_FLEX,
      FlexDirection.FLEX_COL,
      Height.H_FULL,
      Padding.PB_8,
    ),
    wrapper: (bgColor) =>
      classNames(
        JustifyContent.JUSTIFY_START,
        Display.FLEX,
        FlexDirection.FLEX_COL,
        Padding.MD_P_8,
        Padding.MD_PT_0,
        Padding.P_6,
        Padding.PT_0,
        bgColor,
        Height.H_FULL,
      ),
  },
  input: classNames(
    Padding.XS_P_0,
    FlexDirection.FLEX_COL,
    BackgroundColor.BG_NEUTRAL_1,
  ),
  loader: classNames(
    Display.FLEX,
    JustifyContent.JUSTIFY_CENTER,
    AlignItems.ITEMS_CENTER,
    Width.W_FULL,
  ),
  root: (backgroundPosition) =>
    classNames(
      BackgroundSize.BG_COVER,
      BackgroundRepeat.BG_NO_REPEAT,
      Padding.XS_P_0,
      backgroundPosition,
      FontFamily.FONT_BODY,
    ),
};

class LayoutLeftColumn extends React.PureComponent<
  ILayoutLeftColumnProps,
  ILayoutLeftColumnState
> {
  public static displayName = 'LayoutLeftColumn';

  private containerRef;
  static defaultProps = {
    backgroundColor: BackgroundColor.BG_NEUTRAL_2,
    backgroundPosition: BackgroundPosition.BG_RIGHT_TOP,
    loading: false,
  };

  constructor(props) {
    super(props);
    this.containerRef = React.createRef<HTMLDivElement>();
    this.state = {
      backgroundImage: props.backgroundImage,
      loading: props.loading,
    };
  }

  public UNSAFE_componentWillReceiveProps(nextProps) {
    const { backgroundImage: currentBackgroundImage } = this.props;
    const { loading: nextLoading, backgroundImage: nextBackgroundImage } =
      nextProps;

    const newState = {
      loading: nextLoading,
    } as Partial<ILayoutLeftColumnState>;

    if (currentBackgroundImage !== nextBackgroundImage) {
      newState.backgroundImage = nextBackgroundImage;
    }

    if (Object.keys(newState).length > 0) {
      this.setState(newState as ILayoutLeftColumnState);
    }
  }

  public componentDidUpdate(prevProps: Readonly<RouteComponentProps>): void {
    const node = this.containerRef.current;
    if (
      this.props.location.pathname !== prevProps.location.pathname &&
      !this.props.location.pathname.includes('selectpaymentmethod')
    ) {
      node.scrollTop = 0;
    }
  }

  public render() {
    const {
      className,
      tablet,
      mobile,
      showTopImageMobile,
      children,
      backgroundColor,
      backgroundPosition,
      coreHeaderHeight,
    } = this.props;

    const { backgroundImage } = this.state;

    const pageHeight = `calc(100vh - ${coreHeaderHeight})`;

    const gridSpan: GridSpan[] = [];

    if (mobile || tablet) {
      gridSpan.push(GridSpan.ALL_12);
    } else {
      gridSpan.push(GridSpan.ALL_6);
    }

    return (
      <BackgroundLoader
        src={backgroundImage}
        className={styleClass.root(backgroundPosition)}
        style={{ height: pageHeight, overflow: 'scroll' }}
      >
        <div ref={this.containerRef} />
        {/*This is only to be able to create a ref, since <BackgroundLoader/> is a functional component*/}
        <Container width={ContainerWidth.WIDE}>
          <Grid className={styleClass.grid}>
            <GridItem span={gridSpan} className={styleClass.input}>
              {backgroundImage ? (
                <figure
                  className={styleClass.figure(
                    !!(mobile || tablet),
                    showTopImageMobile,
                  )}
                >
                  {backgroundImage && (
                    <img
                      className={styleClass.image}
                      src={backgroundImage}
                      alt={'Illustrativt bilde'}
                    />
                  )}
                </figure>
              ) : null}
              <div
                className={styleClass.inner.wrapper(backgroundColor)}
                style={{ minHeight: pageHeight }}
              >
                <main
                  className={classNames(className, styleClass.inner.container)}
                  id={'main-content'}
                >
                  {children}
                </main>
              </div>
            </GridItem>
          </Grid>
        </Container>
      </BackgroundLoader>
    );
  }
}

export default withRouter<any, any>(
  withResponsiveBrowserOnly(LayoutLeftColumn),
);
